import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { useUnwrappedSelector } from 'hooks/useUnwrappedSelector';
import LoadMoreDropdownOption from 'components/LoadMoreDropdownOption';
import { getCurrentOrganization } from 'rdx/modules/organization/slice';
import { getOrgUsersRaw, requestOrgUsers } from 'rdx/modules/users/slice';
import { OrganizationUserAttributes } from 'types/organizationUsers';
import { UserAttributes } from 'types/user';
import Resource from 'lib/jsonApi/Resource';
import { DropdownOption } from 'components/Dropdown';

export function useOrganizationUsersOptions({ skip = false }) {
  const [ownerSearch, setOwnerSearch] = useState('');
  const searchRef = useRef(ownerSearch);
  const currentOrg = useSelector(getCurrentOrganization);
  const orgUsers = useUnwrappedSelector<OrganizationUserAttributes>(getOrgUsersRaw);

  const [queryPage, setQueryPage] = useState(1);

  const organization_id = useMemo(() => currentOrg?.attributes?.slug ?? null, [currentOrg]);

  const orgUsersOptions = useMemo(
    () => ({
      request: requestOrgUsers,
      params: { organization_id },
      query: { page: queryPage ?? 1 },
      autoFetch: !skip,
    }),
    [organization_id, queryPage, skip]
  );

  const { requestResource: fetchOrgUsers, loading } = useDebouncedSearch(orgUsersOptions);

  useEffect(() => {
    if (searchRef.current !== ownerSearch) {
      setQueryPage(1);
      searchRef.current = ownerSearch;
    }
  }, [ownerSearch]);

  useEffect(() => {
    if (!skip) {
      fetchOrgUsers(ownerSearch);
    }
  }, [ownerSearch, fetchOrgUsers, skip]);

  const userDropdownOptions = useMemo(() => {
    const options: DropdownOption<string>[] = Array.isArray(orgUsers) ? orgUsers.map(
      ({ attributes, getRel }) => {
        const user = getRel<UserAttributes>('user') as Resource<UserAttributes>;
        return {
          value: String(attributes.user_id),
          id: attributes.user_id,
          uuid: user.attributes.uuid,
          display: `${user.attributes.first_name} ${user.attributes.last_name}`,
          attributes: { ...attributes, ...user.attributes },
        }
      }
    ) : [];

    const lastPage = orgUsers?.meta?.pagination?.page === orgUsers?.meta?.pagination?.pageCount;
    if (Array.isArray(orgUsers) && orgUsers.length && !lastPage) {
      options.push({
        value: 'load-more',
        display: '',
        render: () => (
          <LoadMoreDropdownOption
            onClick={(e) => {
              e.stopPropagation();
              setQueryPage((p) => p + 1);
            }}
          >
            <p>Load More</p>
          </LoadMoreDropdownOption>
        ),
      });
    }
    return options;
  }, [orgUsers]);

  return { userDropdownOptions, loading, setOwnerSearch };
}
